import "./pdfStyling/pdfStyling.scss"
import {Localizer} from "../../../infrastructure/localization/localizer";
import { PdfReadySignal } from "./components/pdfReadySignal";
import {useEffect, useState} from "react";
import {useRouteMatch} from "react-router-dom";
import {ForloebModel} from "../../sharedmodels/forloeb/forloebModel";
import {UddannelseTypeEnum} from "../../sharedmodels/stilling/uddannelseTypeEnum";
import PdfComponent from "core/components/pdf/components/pdfComponent";
import {PdfAttestationHeaderComponent} from "./pdfHeaders/pdfAttestationHeaderComponent";
import {useFetchWithErrorHandlingPdf} from "./hooks/useFetchWithErrorHandlingPdf";
import useLogbogApi from "../../hooks/useLogbogApi";
import {UserProfileModel} from "core/components/userProfile/userProfileModel";

export const AttestationForTidPdf = () => {
    const [forloebs, setForloebs] = useState<ForloebModel[]>()
    const [maalbeskrivelse, setMaalbeskrivelse] = useState<string>()
    const [bruger, setBruger] = useState<UserProfileModel>()
    const [dataLoaded, setDataLoaded] = useState(false);

    const routeMatch = useRouteMatch();
    const routeParams = (routeMatch.params as { brugerId: string, brugerMaalbeskrivelseId: string, forloeb?: string });
    const setError = useFetchWithErrorHandlingPdf();
    const { forloebApi, logbogUserInfoApi } = useLogbogApi();

    useEffect(() => {
        const fetchData = async () => {
            const [brugerForloeb, brugerInfo] = await Promise.all([
                forloebApi.getForloebByBrugerMaalbeskrivelse(routeParams.brugerMaalbeskrivelseId),
                logbogUserInfoApi.getUserInfoById(routeParams.brugerId),
            ]);
            if (brugerForloeb !== undefined) {
                const forloeb = brugerForloeb.filter(f => !f.erFravaer).filter(f => !routeParams.forloeb || f.id === routeParams.forloeb);
                setMaalbeskrivelse(forloeb[0].maalbeskrivelseNavn);
                setForloebs(forloeb);
            }
            setBruger(brugerInfo);
            setDataLoaded(true);
        };
        fetchData().catch(setError);
    }, [routeParams.brugerId, routeParams.brugerMaalbeskrivelseId, routeParams.forloeb]);

    const CreateUddannelsestypeStillingsNrDisplayString = (forloeb: ForloebModel) => `${UddannelseTypeEnum[forloeb.uddannelseType]}\n${forloeb.stillingNummer}`;

    return (
        <PdfComponent>
            <h2 className={"font-weight-bolder margin-bottom-l"}>{Localizer.attestationPdf_attestationForTid()}</h2>

            {bruger &&
                <PdfAttestationHeaderComponent
                    data={{ bruger, maalbeskrivelse }}
                />
            }

            <PdfComponent.BlockItem className={"card shadow-none"}>
                <div className={"card-header"}>
                    <div className={"col-2 pdf-list-header"}>{Localizer.uddannelsestrin()}</div>
                    <div className={"col-2 pdf-list-header"}>{Localizer.uddannelsesstedHeaderSplit()}</div>
                    <div className={"col-2 pdf-list-header"}>{Localizer.ansaettelsesperiodeHeaderSplit()}</div>
                    <div className={"col-6 pdf-list-header"}>{Localizer.global_godkendtAf()}</div>
                </div>
                {forloebs &&
                    forloebs.map(forloeb =>
                        <PdfComponent.NonBreakableItem>
                            <div className={"card-body border-bottom"}>
                                <div className={"flex-container-row align-content-center"}>
                                    <div className={"col-2 pdf-list-row-column-item"}>{CreateUddannelsestypeStillingsNrDisplayString(forloeb)}</div>
                                    <div className={"col-2 pdf-list-row-column-item"}>{forloeb.GetStedInfo()}</div>
                                    <div className={"col-2 pdf-list-row-column-item"}>
                                        <div className={"pdf-list-row-column-item"}>{forloeb.startDate.dateWithoutTimeFormat(false) + " - "}</div>
                                        <div className={"pdf-list-row-column-item"}>{forloeb.endDate.dateWithoutTimeFormat(false)}</div>
                                    </div>
                                    <div className={"col-6 pdf-list-row-column-item"}>
                                        {forloeb.godkendelseId &&
                                            <>
                                                <div className={"flex-container-row"}>
                                                    <div className={"font-weight-bold margin-right-xs"}>{Localizer.global_name() + ":"}</div>
                                                    <div>{forloeb.godkendtAfNavn}</div>
                                                </div>

                                                <div className={"flex-container-row"}>
                                                    <div className={"font-weight-bold margin-right-xs"}>{Localizer.dato() + ":"}</div>
                                                    <div>{forloeb.godkendtDen?.dateWithoutTimeFormat(false)}</div>
                                                </div>

                                                <div className={"flex-container-row"}>
                                                    <div className={"font-weight-bold margin-right-xs"}>{Localizer.rolle() + ":"}</div>
                                                    <div>{forloeb.godkendtAfRolleTitel}</div>
                                                </div>

                                                <div className={"flex-container-column"}>
                                                    <div className={"font-weight-bold margin-right-xs align-self-start"}>{Localizer.uddannelsessted() + ":"}</div>
                                                    <div className={"align-self-start"}>{forloeb.godkendtAfUddannelsessted}</div>
                                                </div>

                                                <div className={"flex-container-row"}>
                                                    <div className={"font-weight-bold margin-right-xs"}>{Localizer.global_authorisationId() + ":"}</div>
                                                    <div>{forloeb.godkendtAfAutorisationId}</div>
                                                </div>

                                                {forloeb.godkendtKommentar &&
                                                    <div className={"flex-container-column"}>
                                                        <div className={"font-weight-bold margin-right-xs align-self-start"}>{Localizer.global_comment() + ":"}</div>
                                                        <div className={"align-self-start"}>{forloeb.godkendtKommentar}</div>
                                                    </div>
                                                }
                                            </>
                                        }
                                        {!forloeb.godkendelseId && forloeb.irrelevant &&
                                            <div>{Localizer.forloebpage_ikkeRelevant()}</div>
                                        }
                                    </div>
                                </div>
                                {forloeb.attestationForTidInfoTekst &&
                                    <div className={"flex-container-row margin-top-l"}>
                                        <div className={"font-weight-bold col-2 margin-top-m pdf-list-row-column-item"}>
                                            {Localizer.forloebpage_BemaerkningerTilForloeb()}
                                        </div>
                                        <div className={"col-10 align-content-start margin-top-m margin-bottom-m pdf-list-row-column-item"}>
                                            {forloeb.attestationForTidInfoTekst}
                                        </div>
                                    </div>
                                }
                            </div>
                        </PdfComponent.NonBreakableItem>
                    )
                }
            </PdfComponent.BlockItem>

            <PdfReadySignal when={dataLoaded} />
        </PdfComponent>
    );
}
